import { useStylesHook } from "../hooks/useStylesHook";
import { useThemeContext } from "../theming/useThemeContext";
import { PORTAL_THEME, TMS_THEME } from "../theming/theming";
import { Client } from "../theming/ThemeProvider";
import { BUTTON_BOX_SHADOW, BUTTON_OUTLINE } from "../Button/Button.styles";
import { tone } from "../theming/colors";
import { CONTENT_LABEL } from "../styling/SelectionArea.styles";
import { DISABLED_CURSOR } from "../styling/utils";
const standardLabel = () => ({
    paddingLeft: 20,
    cursor: "pointer",
    fontWeight: "normal!important",
    marginBottom: 4,
    display: "inline-block",
    maxWidth: "100%",
});
const standardInput = (brandedBackground) => ({
    appearance: "none",
    backgroundColor: "#fff",
    margin: [2, 0, 0, 0],
    padding: 0,
    font: "inherit",
    color: TMS_THEME.palette.gray.silver.color,
    width: 14,
    height: 14,
    border: [1, "solid", TMS_THEME.palette.gray.smoke.color],
    borderRadius: "50%",
    transform: "translateY(-0.075em)",
    display: "grid",
    placeContent: "center",
    cursor: "pointer",
    position: "absolute",
    boxShadow: "inset 1px 1px 1px 1px rgba(0, 0, 0, 0.08)",
    boxSizing: "border-box",
    "&:before": {
        content: '""',
        width: 8,
        height: 8,
        borderRadius: "50%",
        transform: "scale(0)",
        transition: "120ms transform ease-in-out",
        boxShadow: `inset 1em 1em ${brandedBackground ?? TMS_THEME.palette.primary.base.color}`,
    },
    "&:checked:before": {
        transform: "scale(1)",
    },
    "&:checked": {
        borderColor: brandedBackground ?? TMS_THEME.palette.primary.base.color,
    },
    "&:disabled": {
        ...standardInputDisabled(),
    },
    "&:disabled:checked": {
        opacity: 0.7,
    },
    "&:disabled:checked:hover": {
        border: [1, "solid", TMS_THEME.palette.primary.base.color],
    },
    "&:focus": {
        outlineColor: brandedBackground ?? TMS_THEME.palette.primary.base.color,
        backgroundColor: brandedBackground ?? TMS_THEME.palette.primary.base.color,
    },
    "&:hover": {
        border: [1, "solid", TMS_THEME.palette.gray.silver.color],
    },
});
const standardLabelDisabled = () => ({
    cursor: DISABLED_CURSOR,
});
const standardInputDisabled = () => ({
    "&:hover": { border: [1, "solid", TMS_THEME.palette.gray.smoke.color] },
    backgroundColor: TMS_THEME.palette.gray.fog.color,
    boxShadow: "unset",
});
const buttonLabel = (client, disabled) => ({
    padding: [client === Client.Portal ? 6 : 5, 10],
    margin: 0,
    marginBottom: "0!important",
    cursor: disabled ? "default" : "pointer",
    fontWeight: TMS_THEME.typography.strong.fontWeight,
});
const buttonLabelDisabled = () => ({
    cursor: DISABLED_CURSOR,
});
const buttonInput = () => ({
    position: "absolute",
    clip: "rect(0, 0, 0, 0)",
    pointerEvents: "none",
});
const buttonSelectionAreaChecked = (buttonStyles) => {
    const alternateState = {
        color: buttonStyles?.primary?.color ?? TMS_THEME.palette.primary.dark.contrast,
        backgroundColor: buttonStyles?.primary?.backgroundColor
            ? tone(buttonStyles?.primary?.backgroundColor, -15)
            : TMS_THEME.palette.primary.dark.color,
    };
    return {
        backgroundColor: buttonStyles?.primary?.backgroundColor ??
            TMS_THEME.palette.primary.base.color,
        color: buttonStyles?.primary?.color ?? TMS_THEME.palette.primary.base.contrast,
        borderColor: buttonStyles?.primary?.color ?? TMS_THEME.palette.primary.dark.color,
        "& + div": {
            borderLeftColor: "rgba(0,0,0,0)",
        },
        "&:hover": alternateState,
    };
};
const buttonSelectionArea = (first, last, buttonStyles, client) => {
    const alternateState = {
        color: TMS_THEME.palette.gray.slate.color,
        backgroundColor: TMS_THEME.palette.gray.fog.color,
    };
    let button = {
        color: TMS_THEME.palette.gray.slate.color,
        backgroundColor: TMS_THEME.palette.gray.white.color,
        position: "relative",
        float: "left",
        borderLeftWidth: 0,
        borderTopWidth: 0,
        borderRightWidth: 0,
        borderBottomWidth: 0,
        borderStyle: "solid",
        borderColor: "rgba(0,0,0,0)",
        verticalAlign: "middle",
        margin: 0,
        borderTopLeftRadius: first ? TMS_THEME.root.borderRadius : 0,
        borderBottomLeftRadius: first ? TMS_THEME.root.borderRadius : 0,
        borderTopRightRadius: last ? TMS_THEME.root.borderRadius : 0,
        borderBottomRightRadius: last ? TMS_THEME.root.borderRadius : 0,
        cursor: "pointer",
        fontSize: TMS_THEME.typography.p.fontSize,
        opacity: 1,
        "&:focus-within": BUTTON_OUTLINE,
        "&:focus": BUTTON_OUTLINE,
        "&:hover": alternateState,
    };
    if (client !== Client.Portal) {
        button = {
            ...button,
            borderColor: TMS_THEME.palette.gray.smoke.color,
            borderLeftWidth: 1,
            borderTopWidth: 1,
            borderRightWidth: last ? 1 : 0,
            borderBottomWidth: 1,
        };
    }
    if (client === Client.Portal) {
        const alternateState = {
            color: TMS_THEME.palette.primary.base.color,
            backgroundColor: TMS_THEME.palette.gray.fog.color,
        };
        button = {
            ...button,
            margin: 0,
            borderTopLeftRadius: first
                ? `${buttonStyles?.default?.borderRadius ??
                    PORTAL_THEME.root.borderRadius}`
                : 0,
            borderBottomLeftRadius: first
                ? `${buttonStyles?.default?.borderRadius ??
                    PORTAL_THEME.root.borderRadius}`
                : 0,
            borderTopRightRadius: last
                ? `${buttonStyles?.default?.borderRadius ??
                    PORTAL_THEME.root.borderRadius}`
                : 0,
            borderBottomRightRadius: last
                ? `${buttonStyles?.default?.borderRadius ??
                    PORTAL_THEME.root.borderRadius}`
                : 0,
            fontSize: PORTAL_THEME.typography.p.fontSize,
            color: TMS_THEME.palette.primary.base.color,
            "&:hover": alternateState,
        };
    }
    return button;
};
const buttonSelectionAreaDisabled = (client) => {
    let button = {
        opacity: 0.5,
        "&:hover": {
            color: TMS_THEME.palette.gray.slate.color,
            backgroundColor: TMS_THEME.palette.gray.white.color,
        },
    };
    if (client === Client.Portal) {
        button = {
            ...button,
            "&:hover": {
                backgroundColor: TMS_THEME.palette.gray.fog.color,
                color: TMS_THEME.palette.primary.base.color,
            },
        };
    }
    return button;
};
const buttonGroup = (client, buttonStyles) => {
    let buttonGroup = {
        position: "relative",
        display: "inline-block",
        verticalAlign: "middle",
        boxShadow: "unset",
        borderRadius: "initial",
    };
    if (client === Client.Portal) {
        buttonGroup = {
            ...buttonGroup,
            boxShadow: BUTTON_BOX_SHADOW,
            borderRadius: `${buttonStyles?.default?.borderRadius ?? PORTAL_THEME.root.borderRadius}`,
        };
    }
    return buttonGroup;
};
const buttonRadioButtons = () => ({
    display: "block",
    width: "100%",
    height: 32,
});
const iconInput = () => ({
    ...standardInput(),
    bottom: 10,
    left: 0,
    right: 0,
    marginLeft: "auto!important",
    marginRight: "auto!important",
});
const iconInputDisabled = () => ({
    opacity: 0.7,
    cursor: DISABLED_CURSOR,
});
const iconLabel = () => ({
    flex: 1,
    minHeight: 56,
    fontStyle: "normal",
    textTransform: "uppercase",
    width: "100%",
    cursor: "pointer",
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    marginBottom: "0px !important",
});
const iconLabelDisabled = () => ({
    opacity: 0.7,
    cursor: DISABLED_CURSOR,
});
export function useRadioButtonClasses({ type, last, first, }) {
    const { client, buttonStyles } = useThemeContext();
    let radioButtons = {};
    let radioButtonsGroup = {};
    let input = {};
    let inputDisabled = {};
    let label = {};
    let labelDisabled = {};
    let buttonArea = {};
    let buttonAreaChecked = {};
    let buttonAreaDisabled = {};
    let buttonAreaCheckedDisabled = {};
    let richBlock = {};
    let richLabel = {};
    let richIcon = {};
    if (type === "standard") {
        label = standardLabel();
        input = standardInput(buttonStyles?.primary?.backgroundColor);
        inputDisabled = standardInputDisabled();
        labelDisabled = standardLabelDisabled();
    }
    if (type === "button") {
        label = buttonLabel(client);
        labelDisabled = buttonLabelDisabled();
        input = buttonInput();
        buttonArea = buttonSelectionArea(first, last, buttonStyles, client);
        buttonAreaDisabled = buttonSelectionAreaDisabled();
        buttonAreaChecked = buttonSelectionAreaChecked(buttonStyles);
        buttonAreaCheckedDisabled = buttonSelectionAreaChecked(buttonStyles);
        radioButtonsGroup = buttonGroup(client, buttonStyles);
        radioButtons = buttonRadioButtons();
    }
    if (type === "icon") {
        inputDisabled = iconInputDisabled();
        labelDisabled = iconLabelDisabled();
        input = iconInput();
        label = iconLabel();
        richLabel = {
            ...CONTENT_LABEL,
            flex: 1,
            textAlign: "center",
            paddingTop: 4,
        };
        richIcon = { flex: 1 };
        richBlock = {
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            minHeight: 60,
            padding: [10, 10, 0, 10],
            width: "100%",
        };
    }
    return useStylesHook({
        radioButtons,
        radioButtonsGroup,
        input,
        label,
        buttonArea,
        buttonAreaChecked,
        richLabel,
        richIcon,
        richBlock,
        inputDisabled,
        labelDisabled,
        buttonAreaDisabled,
        buttonAreaCheckedDisabled,
    });
}
